/**
 * App Mode Configuration
 *
 * Three distinct modes for different use cases:
 *
 * 1. MOCK MODE - Fully offline demonstration
 *    - All data from mock-data.ts
 *    - Simulated authentication (instant login with mock users)
 *    - Simulated AI responses (predefined answers)
 *    - No network calls
 *    - Use: Trade shows, offline demos, development without backend
 *
 * 2. DEMO MODE - Live demonstration with real features
 *    - Real Supabase backend (demo environment)
 *    - Real authentication with demo accounts
 *    - Real AI responses (Anthropic/OpenAI)
 *    - Demo data in database
 *    - Use: Live demos, investor presentations, user testing
 *
 * 3. PRODUCTION MODE - Real application
 *    - Real Supabase backend (production)
 *    - Real authentication
 *    - Real AI responses
 *    - Real user data
 *    - Use: Production deployment
 *
 * Priority chain for mode determination:
 *   1. Web hostname (demo.inverio.ai -> demo) — cannot be overridden
 *   2. EXPO_PUBLIC_APP_MODE env var — cannot be overridden (S15: the
 *      persisted override is client-writable storage and must never
 *      outrank the build's declared mode)
 *   3. Persisted override from SecureStore/localStorage
 *   4. Legacy EXPO_PUBLIC_DEMO_MODE support
 *   5. Auto-detect (Supabase configured -> production, else mock)
 */

import { Platform } from 'react-native';

export type AppMode = 'mock' | 'demo' | 'production';

const MODE_OVERRIDE_KEY = 'inverio_app_mode_override';

// ---------------------------------------------------------------------------
// Synchronous override storage
// ---------------------------------------------------------------------------

/**
 * Read persisted mode override synchronously.
 * - Native: expo-secure-store getItem() is synchronous in SDK 53
 * - Web: localStorage.getItem() is synchronous
 */
function readModeOverrideSync(): AppMode | null {
  try {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        const val = window.localStorage.getItem(MODE_OVERRIDE_KEY);
        if (val && isValidMode(val)) return val as AppMode;
      }
      return null;
    }
    // Native: use expo-secure-store synchronous API
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const SecureStore = require('expo-secure-store');
    if (SecureStore.getItem) {
      const val = SecureStore.getItem(MODE_OVERRIDE_KEY);
      if (val && isValidMode(val)) return val as AppMode;
    }
    return null;
  } catch {
    return null;
  }
}

function isValidMode(val: string): val is AppMode {
  return val === 'mock' || val === 'demo' || val === 'production';
}

// ---------------------------------------------------------------------------
// Mode determination
// ---------------------------------------------------------------------------

// Read mode from environment
const APP_MODE_ENV = process.env.EXPO_PUBLIC_APP_MODE as AppMode | undefined;

// Legacy support: DEMO_MODE=true maps to 'mock' mode for backwards compatibility
const LEGACY_DEMO_MODE = process.env.EXPO_PUBLIC_DEMO_MODE === 'true';

// Web hostname -> app mode mapping for subdomain-based routing
const HOSTNAME_MODE_MAP: Record<string, AppMode> = {
  'demo.inverio.ai': 'demo',
  'mock.inverio.ai': 'mock',
  'www.inverio.ai': 'production',
  'inverio.ai': 'production',
};

function getWebHostnameMode(): AppMode | undefined {
  if (typeof window === 'undefined' || !window.location) return undefined;
  return HOSTNAME_MODE_MAP[window.location.hostname];
}

/** Whether current mode was set by hostname (cannot be overridden in-app) */
let _hostnameControlled = false;

/** Whether the mode was pinned by an explicit env var (cannot be overridden) */
let _envControlled = false;

/** The persisted override, if any (read synchronously at module init) */
let _currentOverride: AppMode | null = null;

function determineAppMode(): AppMode {
  // 1. Web hostname override takes priority (subdomain-based routing)
  const hostnameMode = getWebHostnameMode();
  if (hostnameMode) {
    _hostnameControlled = true;
    return hostnameMode;
  }

  // 2. Explicit env var mode — outranks the persisted override (S15): the
  // override lives in client-writable storage, so it must never beat the
  // build's declared mode. Env-pinned builds cannot be switched at runtime.
  if (APP_MODE_ENV && isValidMode(APP_MODE_ENV)) {
    _envControlled = true;
    return APP_MODE_ENV;
  }

  // 3. Persisted override from SecureStore/localStorage
  _currentOverride = readModeOverrideSync();
  if (_currentOverride) {
    return _currentOverride;
  }

  // 4. Legacy support: DEMO_MODE=true means mock mode
  if (LEGACY_DEMO_MODE) {
    return 'mock';
  }

  // 5. Check if Supabase is configured
  const hasSupabase = Boolean(
    process.env.EXPO_PUBLIC_SUPABASE_URL && process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY
  );

  // Default to production if Supabase is configured, otherwise mock
  return hasSupabase ? 'production' : 'mock';
}

// Use `let` so ESM live bindings propagate to importers on restart
// eslint-disable-next-line prefer-const
export let APP_MODE: AppMode = determineAppMode();

/** Whether the mode is controlled by web hostname (cannot be overridden) */
export const isHostnameControlled = (): boolean => _hostnameControlled;

/** Whether the mode is pinned by EXPO_PUBLIC_APP_MODE (cannot be overridden) */
export const isEnvControlled = (): boolean => _envControlled;

/** Whether there is an active persisted override */
export const hasActiveOverride = (): boolean => _currentOverride !== null;

/**
 * Compute what the default mode would be without any override.
 * Useful for showing "default: mock" in the dev toggle UI.
 */
export function getDefaultMode(): AppMode {
  const hostnameMode = getWebHostnameMode();
  if (hostnameMode) return hostnameMode;

  if (APP_MODE_ENV && isValidMode(APP_MODE_ENV)) return APP_MODE_ENV;
  if (LEGACY_DEMO_MODE) return 'mock';

  const hasSupabase = Boolean(
    process.env.EXPO_PUBLIC_SUPABASE_URL && process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY
  );
  return hasSupabase ? 'production' : 'mock';
}

// ---------------------------------------------------------------------------
// Override persistence (async — call then restart)
// ---------------------------------------------------------------------------

/**
 * Save a mode override. After calling this, restart the app for it to take effect.
 */
export async function saveModeOverride(mode: AppMode): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(MODE_OVERRIDE_KEY, mode);
    }
    return;
  }
  // Native: use expo-secure-store async API
  const SecureStore = require('expo-secure-store');
  await SecureStore.setItemAsync(MODE_OVERRIDE_KEY, mode);
}

/**
 * Clear any persisted mode override. After calling this, restart the app
 * to revert to the default mode.
 */
export async function clearModeOverride(): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(MODE_OVERRIDE_KEY);
    }
    return;
  }
  const SecureStore = require('expo-secure-store');
  await SecureStore.deleteItemAsync(MODE_OVERRIDE_KEY);
}

// ---------------------------------------------------------------------------
// App restart
// ---------------------------------------------------------------------------

/**
 * Restart the app so the new mode takes effect.
 * - Web: window.location.reload()
 * - Native dev: DevSettings.reload() (fast refresh)
 * - Native prod: Updates.reloadAsync() (full reload via expo-updates)
 */
export async function restartApp(): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
    return;
  }

  // Native
  if (__DEV__) {
    // Use DevSettings for fast reload in development
    const { DevSettings } = require('react-native');
    DevSettings.reload();
  } else {
    // Use expo-updates for production reload
    try {
      const Updates = require('expo-updates');
      await Updates.reloadAsync();
    } catch {
      // Fallback: some builds may not have expo-updates
      const { DevSettings } = require('react-native');
      DevSettings.reload();
    }
  }
}

// ---------------------------------------------------------------------------
// Runtime demo bypass
// ---------------------------------------------------------------------------

/**
 * Runtime demo bypass — activated by "Try Demo" login to use mock data
 * even when APP_MODE is 'demo' or 'production'.
 */
let _demoBypass = false;
export const activateDemoBypass = () => { _demoBypass = true; };
export const deactivateDemoBypass = () => { _demoBypass = false; };

// ---------------------------------------------------------------------------
// Mode checks
// ---------------------------------------------------------------------------

/** True when using mock data and simulated features (offline capable) */
export const isMockMode = (): boolean => APP_MODE === 'mock' || _demoBypass;

/** True when using real backend with demo accounts */
export const isDemoMode = (): boolean => APP_MODE === 'demo';

/**
 * True when demo mode was declared authoritatively by the build or host
 * (EXPO_PUBLIC_APP_MODE=demo or a demo hostname) — NOT by the client-writable
 * persisted override.
 *
 * Security (S11): the bundled demo credentials must only ever auto-login when
 * demo mode is trusted. A persisted override lives in client-writable storage
 * (SecureStore/localStorage), so demo mode reached that way is untrusted — a
 * production-origin build flipped to demo via the override must never auto-use
 * demo credentials, even if those creds ever leak back into a prod bundle.
 */
export const isDemoModeTrusted = (): boolean =>
  isDemoMode() && (isEnvControlled() || isHostnameControlled());

/** True when UI should display sample/mock data (mock mode, demo mode, or demo bypass) */
export const shouldShowSampleData = (): boolean => APP_MODE === 'mock' || APP_MODE === 'demo' || _demoBypass;

/** True when in full production mode */
export const isProductionMode = (): boolean => APP_MODE === 'production';

/** True when backend (Supabase) should be used - demo OR production */
export const isBackendEnabled = (): boolean => APP_MODE !== 'mock';

/** True when real AI should be used - demo OR production */
export const useRealAI = (): boolean => APP_MODE !== 'mock';

/** True when real authentication should be used - demo OR production */
export const useRealAuth = (): boolean => APP_MODE !== 'mock';

// ---------------------------------------------------------------------------
// Beta mode
// ---------------------------------------------------------------------------

/** Whether the app is in beta (invite-only signups, waitlist landing page) */
export const BETA_MODE = process.env.EXPO_PUBLIC_BETA_MODE !== 'false'; // default true

/** Check beta mode status */
export const isBetaMode = (): boolean => BETA_MODE;

// ---------------------------------------------------------------------------
// Feature flags (getter-based for dynamic evaluation)
// ---------------------------------------------------------------------------

/**
 * Feature flags based on mode — uses getters so values are always
 * computed from the current APP_MODE, not stale snapshots.
 */
export const modeFeatures = {
  /** Whether to use mock data from mock-data.ts (mock + demo modes) */
  get useMockData() { return shouldShowSampleData(); },

  /** Whether to connect to Supabase */
  get useSupabase() { return isBackendEnabled(); },

  /** Whether to use real AI (Anthropic/OpenAI) */
  get useRealAI() { return useRealAI(); },

  /** Whether to use real authentication */
  get useRealAuth() { return useRealAuth(); },

  /** Whether to enable real-time subscriptions */
  get useRealtime() { return isBackendEnabled(); },

  /** Whether to allow data writes to backend */
  get allowDataWrites() { return isBackendEnabled(); },

  /** Whether to use real file storage */
  get useRealStorage() { return isBackendEnabled(); },

  /** Whether to enable Stripe payments */
  get useStripePayments() { return isProductionMode(); },

  /** Whether to show demo account picker */
  get showDemoAccountPicker() { return isDemoMode(); },

  /** Whether to show mock/demo mode indicator */
  get showMockIndicator() { return shouldShowSampleData(); },

  /** Whether signup is invite-only (beta mode) */
  get inviteOnly() { return isBetaMode(); },

  /** Whether to show the beta landing page instead of the launch page */
  get showBetaLanding() { return isBetaMode(); },

  /**
   * Whether the public site (logged-out visitors, crawlers) shows plan prices:
   * the /pricing page and its links. The beta sells nothing publicly.
   * A marketing decision, separate from — and narrower than — the permanent
   * web-only commerce rule in src/lib/commerce/policy.ts, which still applies.
   * public/sitemap.xml is static: re-add its /pricing entry when the beta ends.
   */
  get showPublicPricing() { return !isBetaMode(); },
};

// ---------------------------------------------------------------------------
// Mode descriptions
// ---------------------------------------------------------------------------

export const modeDescriptions: Record<AppMode, { label: string; description: string; emoji: string }> = {
  mock: {
    label: 'Mock Mode',
    description: 'Offline demo with simulated data',
    emoji: '🎮',
  },
  demo: {
    label: 'Demo Mode',
    description: 'Live demo with real features',
    emoji: '🎯',
  },
  production: {
    label: 'Production',
    description: 'Live application',
    emoji: '🚀',
  },
};

// ---------------------------------------------------------------------------
// Mode adapter (single-object facade)
// ---------------------------------------------------------------------------

export { mode } from './mode-adapter';

// Log current mode on startup (dev only)
if (__DEV__) {
  const modeInfo = modeDescriptions[APP_MODE];
  console.log(`${modeInfo.emoji} Running in ${modeInfo.label.toUpperCase()} - ${modeInfo.description}`);

  if (_currentOverride) {
    console.log(`   Mode override active (default would be: ${getDefaultMode()})`);
  }

  if (APP_MODE === 'mock') {
    console.log('   Mock data and simulated responses will be used.');
    console.log('   Set EXPO_PUBLIC_APP_MODE=demo or =production to use real backend.');
  }
}
